import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Truck,
  CheckCircle,
  Clock,
  MapPin,
  Package,
  Eye,
  RefreshCw,
  Calendar,
  User,
  FileText
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const ORDER_STATUS = {
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-700' },
  approved: { label: 'Approved', color: 'bg-blue-100 text-blue-700' },
  delivered: { label: 'Delivered', color: 'bg-green-100 text-green-700' },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700' }
};

const NASDeliveryConfirmation = () => {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('approved');
  const [viewingOrder, setViewingOrder] = useState(null);
  
  // Delivery confirmation state
  const [confirmingOrder, setConfirmingOrder] = useState(null);
  const [deliveryNotes, setDeliveryNotes] = useState('');
  const [isConfirming, setIsConfirming] = useState(false);

  // Users who can confirm deliveries: Admin, Jim, and Jerome
  const authorizedDeliveryUsers = ['arbe', 'admin', 'jim', 'jerome'];
  const canConfirmDelivery = user?.role === 'admin' || 
    authorizedDeliveryUsers.some(name => 
      user?.username?.toLowerCase().includes(name) || 
      user?.full_name?.toLowerCase().includes(name)
    );

  const formatCurrency = (amount) => {
    return `₱${(amount || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}`;
  };

  const loadOrders = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${API}/api/nas/customer-orders`);
      // Filter to show approved and delivered orders
      const deliveryOrders = (res.data || []).filter(o => 
        ['approved', 'delivered'].includes(o.status)
      );
      setOrders(deliveryOrders);
    } catch (error) {
      console.error('Error loading orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadOrders();
    // eslint-disable-next-line
  }, []);

  const handleConfirmDelivery = async () => {
    if (!deliveryNotes.trim()) {
      toast.error('Please add delivery notes before confirming');
      return;
    }
    
    setIsConfirming(true);
    try {
      await axios.put(`${API}/api/nas/customer-orders/${confirmingOrder.id}/deliver`, {
        deliveryNotes,
        receivedBy: ''
      });
      toast.success('Delivery confirmed!');
      setConfirmingOrder(null);
      setDeliveryNotes('');
      loadOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm delivery');
    } finally {
      setIsConfirming(false);
    }
  };

  const filteredOrders = orders.filter(o => 
    statusFilter === 'all' || o.status === statusFilter
  );

  const approvedCount = orders.filter(o => o.status === 'approved').length;
  const deliveredCount = orders.filter(o => o.status === 'delivered').length;

  if (!canConfirmDelivery) {
    return (
      <div className="p-6 text-center">
        <Truck size={48} className="mx-auto text-slate-300 mb-4" />
        <h2 className="text-xl font-semibold text-slate-700 mb-2">Access Restricted</h2>
        <p className="text-slate-500">Only Admin, Jim and Jerome (drivers) can access delivery confirmations.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 gap-4">
        <Card 
          className={`cursor-pointer transition-all ${statusFilter === 'approved' ? 'ring-2 ring-blue-500' : ''}`}
          onClick={() => setStatusFilter('approved')}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Clock className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Ready for Delivery</p>
                <p className="text-2xl font-bold text-blue-700">{approvedCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card 
          className={`cursor-pointer transition-all ${statusFilter === 'delivered' ? 'ring-2 ring-green-500' : ''}`}
          onClick={() => setStatusFilter('delivered')}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Delivered</p>
                <p className="text-2xl font-bold text-green-700">{deliveredCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter and Refresh */}
      <div className="flex items-center justify-between">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Orders</SelectItem>
            <SelectItem value="approved">Ready for Delivery</SelectItem>
            <SelectItem value="delivered">Delivered</SelectItem>
          </SelectContent>
        </Select>
        
        <Button variant="outline" onClick={loadOrders} disabled={isLoading}>
          <RefreshCw size={16} className={`mr-2 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Orders List */}
      <div className="space-y-3">
        {isLoading ? (
          <div className="text-center py-12">
            <RefreshCw size={32} className="mx-auto animate-spin text-slate-400 mb-2" />
            <p className="text-slate-500">Loading orders...</p>
          </div>
        ) : filteredOrders.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Truck size={48} className="mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">No orders found for this filter</p>
            </CardContent>
          </Card>
        ) : (
          filteredOrders.map(order => (
            <Card key={order.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                  {/* Order Info */}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-bold text-lg">{order.orderNumber}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs ${ORDER_STATUS[order.status]?.color}`}>
                        {ORDER_STATUS[order.status]?.label}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                      <div className="flex items-center gap-1 text-slate-600">
                        <User size={14} />
                        <span>{order.customerName || 'Unknown'}</span>
                      </div>
                      <div className="flex items-center gap-1 text-slate-600">
                        <Package size={14} />
                        <span>{order.items?.length || 0} items</span>
                      </div>
                      <div className="flex items-center gap-1 text-slate-600">
                        <Calendar size={14} />
                        <span>{order.deliveryDate || 'No date'}</span>
                      </div>
                      <div className="flex items-center gap-1 text-slate-600">
                        <MapPin size={14} />
                        <span className="truncate">{order.deliveryAddress || 'No address'}</span>
                      </div>
                    </div>
                    
                    <div className="mt-2 font-semibold text-emerald-600">
                      {formatCurrency(order.totalAmount)}
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex gap-2 flex-wrap">
                    <Button variant="outline" size="sm" onClick={() => setViewingOrder(order)}>
                      <Eye size={16} className="mr-1" /> View
                    </Button>
                    
                    {order.status === 'approved' && (
                      <Button 
                        size="sm" 
                        className="bg-green-600 hover:bg-green-700"
                        onClick={() => {
                          setConfirmingOrder(order);
                          setDeliveryNotes('');
                        }}
                      >
                        <CheckCircle size={16} className="mr-1" /> Mark as Delivered
                      </Button>
                    )}
                    
                    {order.status === 'delivered' && (
                      <div className="flex flex-col text-sm text-green-600">
                        <span className="flex items-center">
                          <CheckCircle size={16} className="mr-1" />
                          Delivered {order.deliveredAt ? new Date(order.deliveredAt).toLocaleDateString() : ''}
                        </span>
                        {order.deliveryNotes && (
                          <span className="text-xs text-slate-500 flex items-center mt-1">
                            <FileText size={12} className="mr-1" />
                            {order.deliveryNotes}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* View Order Dialog */}
      <Dialog open={!!viewingOrder} onOpenChange={() => setViewingOrder(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Order Details - {viewingOrder?.orderNumber}</DialogTitle>
          </DialogHeader>
          
          {viewingOrder && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold">{viewingOrder.customerName}</p>
                  <p className="text-sm text-slate-500">{viewingOrder.deliveryAddress}</p>
                </div>
                <span className={`px-3 py-1 rounded-full ${ORDER_STATUS[viewingOrder.status]?.color}`}>
                  {ORDER_STATUS[viewingOrder.status]?.label}
                </span>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">Delivery Date</p>
                  <p className="font-medium">{viewingOrder.deliveryDate || 'Not set'}</p>
                </div>
                <div>
                  <p className="text-slate-500">Payment Terms</p>
                  <p className="font-medium capitalize">{viewingOrder.paymentTerms || 'COD'}</p>
                </div>
              </div>
              
              <div>
                <p className="font-semibold mb-2">Items</p>
                <div className="bg-slate-50 rounded-lg p-3 space-y-2">
                  {viewingOrder.items?.map((item, idx) => (
                    <div key={idx} className="flex justify-between">
                      <span>{item.name}</span>
                      <span className="font-medium">{item.quantity} × {formatCurrency(item.pricePerUnit)}</span>
                    </div>
                  ))}
                  <div className="border-t pt-2 mt-2 flex justify-between font-bold">
                    <span>Total</span>
                    <span className="text-emerald-600">{formatCurrency(viewingOrder.totalAmount)}</span>
                  </div>
                </div>
              </div>
              
              {viewingOrder.notes && (
                <div>
                  <p className="text-slate-500 text-sm">Order Notes</p>
                  <p>{viewingOrder.notes}</p>
                </div>
              )}
              
              {viewingOrder.status === 'delivered' && (
                <div className="bg-green-50 p-3 rounded-lg">
                  <p className="font-semibold text-green-700 flex items-center gap-2">
                    <CheckCircle size={16} /> Delivered
                  </p>
                  <p className="text-sm text-green-600">
                    {new Date(viewingOrder.deliveredAt).toLocaleString('en-PH')}
                  </p>
                  <p className="text-sm">By: {viewingOrder.deliveredBy}</p>
                  {viewingOrder.deliveryNotes && (
                    <p className="text-sm mt-2 p-2 bg-white rounded border">
                      <span className="font-medium">Notes:</span> {viewingOrder.deliveryNotes}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewingOrder(null)}>Close</Button>
            {viewingOrder?.status === 'approved' && (
              <Button 
                className="bg-green-600 hover:bg-green-700"
                onClick={() => {
                  setViewingOrder(null);
                  setConfirmingOrder(viewingOrder);
                  setDeliveryNotes('');
                }}
              >
                <CheckCircle size={16} className="mr-1" /> Mark as Delivered
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Delivery Dialog */}
      <Dialog open={!!confirmingOrder} onOpenChange={() => setConfirmingOrder(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="text-green-600" />
              Confirm Delivery
            </DialogTitle>
          </DialogHeader>
          
          {confirmingOrder && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-semibold">{confirmingOrder.orderNumber}</p>
                <p className="text-sm text-slate-600">{confirmingOrder.customerName}</p>
                <p className="text-sm text-slate-600">{confirmingOrder.deliveryAddress}</p>
                <p className="font-bold text-emerald-600 mt-2">{formatCurrency(confirmingOrder.totalAmount)}</p>
              </div>
              
              <div className="space-y-2">
                <Label className="flex items-center gap-1">
                  <FileText size={14} />
                  Delivery Notes <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  value={deliveryNotes}
                  onChange={(e) => setDeliveryNotes(e.target.value)}
                  placeholder="Enter delivery notes (e.g., Received by Juan, left at gate, etc.)"
                  rows={3}
                  className="resize-none"
                />
                <p className="text-xs text-slate-500">Required: Please add notes before confirming delivery</p>
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirmingOrder(null)}>
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmDelivery}
              disabled={isConfirming || !deliveryNotes.trim()}
              className="bg-green-600 hover:bg-green-700"
            >
              {isConfirming ? 'Confirming...' : 'Confirm Delivery'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NASDeliveryConfirmation;
